import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anonymous
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (_e) {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // sessionStorage: safer than localStorage (cleared on tab close, not
      // persistent across sessions), used as a fallback for mobile browsers
      // that block SameSite=None httpOnly cookies.
      if (data.access_token) {
        try { sessionStorage.setItem("atech_access_token", data.access_token); } catch (_e) { /* storage unavailable */ }
      }
      const { access_token, ...userData } = data; // eslint-disable-line no-unused-vars
      setUser(userData);
      return userData;
    } catch (e) {
      // Surface a useful message instead of the bland fallback.
      let msg;
      if (e.response?.data?.detail != null) {
        msg = formatApiErrorDetail(e.response.data.detail);
      } else if (e.response) {
        msg = `Server error (${e.response.status}). Please try again or contact support.`;
      } else if (e.request) {
        msg = "Couldn't reach the server. Check your connection and try again.";
      } else {
        msg = e.message || "Something went wrong. Please try again.";
      }
      setError(msg);
      throw new Error(msg);
    }
  }, []); // setError/setUser are stable React state setters; api is module-level

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (_e) { /* cookies are cleared client-side regardless of server response */ }
    try { sessionStorage.removeItem("atech_access_token"); } catch (_e) { /* storage unavailable */ }
    setUser(false);
  }, []); // setUser is a stable state setter; api is module-level

  const value = useMemo(
    () => ({ user, error, login, logout, refresh }),
    [user, error, login, logout, refresh],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
